/**
 * The API keys tab of Integrations (`api.key.manage.own`): the person's keys as the MCP tokens list shows its tokens
 * (expired flagged, revoked gone), with Create API key, Extend and Revoke. The same table, dialogs and rules as tokens:
 * a key is a token for the integration API.
 */

import { useState } from 'react';

import { useApiKeys, useRevokeApiKey } from '@/api/queries';
import type { ApiKeyOut } from '@/api/generated/schemas';
import type { McpToken } from '@/api/types';
import { CreateTokenDialog } from '@/routes/account/CreateTokenDialog';
import { ExtendTokenDialog } from '@/routes/account/ExtendTokenDialog';
import { TokensTable } from '@/routes/account/TokensTable';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { sortRows } from '@/ui/sortRows';
import { useSort } from '@/ui/useSort';
import { ASCENDING_FIRST, TOKEN_SORT } from '@/routes/account/tokenSort';
import { Plus } from 'lucide-react';

/**
 * A key as the token table shows a personal token: it is one, for another surface. `ApiKeyOut` has no `kind` (a key is
 * always personal), but the shared table and dialogs take an `McpToken`, which needs one: the row adds it.
 */
function asRow(key: ApiKeyOut): McpToken {
  return { ...key, kind: 'personal' };
}

function RevokeKeyDialog({ token, onClose }: { token: McpToken; onClose: () => void }) {
  const revoke = useRevokeApiKey();
  return (
    <ConfirmDialog
      title="Revoke this API key?"
      confirmLabel="Revoke key"
      danger
      pending={revoke.isPending}
      error={revoke.error}
      onConfirm={() => revoke.mutate(token.id, { onSuccess: onClose })}
      onClose={onClose}
    >
      <p>Any system using “{token.name}” stops working right away. This cannot be undone.</p>
    </ConfirmDialog>
  );
}

function KeysList({ keys, onExtend, onRevoke }: { keys: McpToken[]; onExtend: (key: McpToken) => void; onRevoke: (key: McpToken) => void }) {
  const { sort, order, onSort } = useSort('created', 'desc', ASCENDING_FIRST);
  return (
    <TokensTable
      tokens={sortRows(keys, sort, order, TOKEN_SORT)}
      empty="You have no API keys yet. Create one, then follow the API guide."
      sort={sort}
      order={order}
      onSort={onSort}
      onExtend={onExtend}
      onRevoke={onRevoke}
    />
  );
}

export function ApiKeysTab() {
  const keys = useApiKeys();
  const [creating, setCreating] = useState(false);
  const [extending, setExtending] = useState<McpToken | null>(null);
  const [revoking, setRevoking] = useState<McpToken | null>(null);
  return (
    <>
      <div className="toolbar">
        <p className="hint">
          A key lets an internal GFT system call the integration API as you, within the permissions you tick. Like your MCP
          tokens, it stops working if you don’t sign in to Wynxx Discover for a week: then create a new one.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" /> Create API key
        </button>
      </div>
      {keys.isPending && <LoadingCard label="Loading API keys" />}
      {!keys.isPending && keys.error && <LoadFailure error={keys.error} onRetry={() => void keys.refetch()} />}
      {keys.data && <KeysList keys={keys.data.keys.map(asRow)} onExtend={setExtending} onRevoke={setRevoking} />}
      {creating && <CreateTokenDialog kind="key" onClose={() => setCreating(false)} />}
      {extending && <ExtendTokenDialog kind="key" token={extending} onClose={() => setExtending(null)} />}
      {revoking && <RevokeKeyDialog token={revoking} onClose={() => setRevoking(null)} />}
    </>
  );
}
